/**
 * Outil « Urne et tirages » — calculs (pur).
 *
 * Une urne de boules rouges, bleues et vertes ; k tirages successifs (2 ou 3)
 * avec ou sans remise. Toutes les probabilités sont EXACTES et partagent un
 * même dénominateur : le nombre de tirages ordonnés de boules numérotées,
 * Nᵏ avec remise, N(N − 1)…(N − k + 1) sans remise. Un chemin de l'arbre
 * vaut (produit des effectifs disponibles) / D, un événement la somme de ses
 * chemins : c'est aussi « favorables / possibles ». Loi du nombre X de boules
 * d'une couleur : binomiale avec remise, hypergéométrique sans remise ;
 * E(X) = k·r/N dans les deux cas.
 */

import { binomCoeff, pickWeighted } from '../../ui/random';

export interface UrnColor {
  /** lettre écrite dans la boule et dans les noms de chemins */
  id: 'R' | 'B' | 'V';
  name: string;
  plural: string;
  fill: string;
  ink: string;
}

/** couleurs « physiques » des boules (mêmes en thème clair et sombre), lettre blanche */
export const COLORS: readonly UrnColor[] = [
  { id: 'R', name: 'rouge', plural: 'rouges', fill: '#d1343a', ink: '#ffffff' },
  { id: 'B', name: 'bleue', plural: 'bleues', fill: '#2d5bc9', ink: '#ffffff' },
  { id: 'V', name: 'verte', plural: 'vertes', fill: '#1f7a50', ink: '#ffffff' },
];

export const MAX_PER_COLOR = 9;

export interface UrnSetup {
  /** effectifs par couleur, dans l'ordre de COLORS */
  counts: readonly number[];
  /** nombre de tirages successifs */
  k: number;
  replace: boolean;
}

export const total = (counts: readonly number[]) => counts.reduce((s, n) => s + n, 0);

/** l'expérience a un sens : au moins une boule, et assez de boules sans remise */
export function feasible({ counts, k, replace }: UrnSetup): boolean {
  const n = total(counts);
  return n > 0 && (replace || n >= k);
}

/** effectifs encore dans l'urne après les boules `drawn` (sans remise) */
export function remaining(counts: readonly number[], drawn: readonly number[], replace: boolean) {
  if (replace) return [...counts];
  const left = [...counts];
  for (const c of drawn) left[c]!--;
  return left;
}

/** dénominateur commun : Nᵏ (k-uplets) ou N(N − 1)…(N − k + 1) (arrangements) */
export function denominator({ counts, k, replace }: UrnSetup): number {
  const n = total(counts);
  let d = 1;
  for (let i = 0; i < k; i++) d *= replace ? n : n - i;
  return d;
}

export interface Branch {
  /** couleur tirée à ce niveau */
  color: number;
  /** effectif disponible / boules dans l'urne au moment du tirage */
  num: number;
  den: number;
}

export interface UrnPath {
  colors: number[];
  branches: Branch[];
  /** numérateur sur le dénominateur commun (= nombre de tirages ordonnés favorables) */
  num: number;
}

/** Tous les chemins de probabilité non nulle, dans l'ordre de l'arbre. */
export function allPaths(setup: UrnSetup): UrnPath[] {
  if (!feasible(setup)) return [];
  const out: UrnPath[] = [];
  const walk = (colors: number[], branches: Branch[]) => {
    if (colors.length === setup.k) {
      out.push({ colors, branches, num: branches.reduce((p, b) => p * b.num, 1) });
      return;
    }
    const left = remaining(setup.counts, colors, setup.replace);
    const den = total(left);
    left.forEach((num, color) => {
      if (num > 0) walk([...colors, color], [...branches, { color, num, den }]);
    });
  };
  walk([], []);
  return out;
}

export type EventId = 'same' | 'atLeast' | 'none' | 'exactlyOne' | 'second' | 'different';

export interface UrnEvent {
  id: EventId;
  /** la couleur suivie compte-t-elle ? */
  usesColor: boolean;
  label: (colorName: string, k: number) => string;
  test: (colors: readonly number[], c: number) => boolean;
}

export const EVENTS: readonly UrnEvent[] = [
  {
    id: 'same',
    usesColor: false,
    label: (_, k) => (k === 2 ? 'Même couleur' : 'Les 3 de même couleur'),
    test: (p) => p.every((x) => x === p[0]),
  },
  {
    id: 'different',
    usesColor: false,
    label: (_, k) => (k === 2 ? 'Couleurs différentes' : 'Trois couleurs différentes'),
    test: (p) => new Set(p).size === p.length,
  },
  {
    id: 'atLeast',
    usesColor: true,
    label: (c) => `Au moins une ${c}`,
    test: (p, c) => p.includes(c),
  },
  {
    id: 'none',
    usesColor: true,
    label: (c) => `Aucune ${c}`,
    test: (p, c) => !p.includes(c),
  },
  {
    id: 'exactlyOne',
    usesColor: true,
    label: (c) => `Exactement une ${c}`,
    test: (p, c) => p.filter((x) => x === c).length === 1,
  },
  {
    id: 'second',
    usesColor: true,
    label: (c) => `La 2e est ${c}`,
    test: (p, c) => p[1] === c,
  },
];

export function eventById(id: EventId): UrnEvent {
  return EVENTS.find((e) => e.id === id)!;
}

/** « RB », « RRV » : le nom d'un chemin */
export const pathName = (colors: readonly number[]) => colors.map((c) => COLORS[c]!.id).join('');

export interface EventResult {
  /** chemins qui réalisent l'événement */
  paths: UrnPath[];
  /** somme de leurs numérateurs (= tirages ordonnés favorables) */
  num: number;
  den: number;
}

export function eventProbability(setup: UrnSetup, id: EventId, c: number): EventResult {
  const ev = eventById(id);
  const paths = allPaths(setup).filter((p) => ev.test(p.colors, c));
  return { paths, num: paths.reduce((s, p) => s + p.num, 0), den: denominator(setup) };
}

/** k parmi n (petits n) */
export const choose = binomCoeff;

/**
 * Loi du nombre X de boules de la couleur c lue SUR L'ARBRE : P(X = j) =
 * (somme des chemins à j boules c) / D, D le dénominateur commun (1re).
 */
export function lawFromPaths(setup: UrnSetup, c: number): { num: number[]; den: number } {
  const num = Array.from({ length: setup.k + 1 }, () => 0);
  for (const q of allPaths(setup)) num[q.colors.filter((x) => x === c).length]! += q.num;
  return { num, den: denominator(setup) };
}

/**
 * Loi du nombre X de boules de la couleur c : P(X = j) = num[j] / den.
 * Avec remise : C(k, j) rʲ (N − r)ᵏ⁻ʲ / Nᵏ ; sans : C(r, j) C(N − r, k − j) / C(N, k).
 */
export function countLaw(setup: UrnSetup, c: number): { num: number[]; den: number } {
  const n = total(setup.counts);
  const r = setup.counts[c] ?? 0;
  const { k } = setup;
  if (setup.replace) {
    return {
      num: Array.from({ length: k + 1 }, (_, j) => choose(k, j) * r ** j * (n - r) ** (k - j)),
      den: n ** k,
    };
  }
  return {
    num: Array.from({ length: k + 1 }, (_, j) => choose(r, j) * choose(n - r, k - j)),
    den: choose(n, k),
  };
}

/** E(X) = k·r/N (avec ou sans remise), en fraction (num, den) */
export function expectation(setup: UrnSetup, c: number): { num: number; den: number } {
  return { num: setup.k * (setup.counts[c] ?? 0), den: total(setup.counts) };
}

/**
 * V(X) en fraction : k·p(1 − p) avec remise ; × (N − k)/(N − 1) sans remise.
 * (N = 1 sans remise : k = 1, variance nulle.)
 */
export function variance(setup: UrnSetup, c: number): { num: number; den: number } {
  const n = total(setup.counts);
  const r = setup.counts[c] ?? 0;
  const { k } = setup;
  if (setup.replace || n === 1) return { num: k * r * (n - r), den: n * n };
  return { num: k * r * (n - r) * (n - k), den: n * n * (n - 1) };
}

/** Une boule tirée parmi celles qui restent. */
export function drawOne(
  counts: readonly number[],
  drawn: readonly number[],
  replace: boolean,
  rng: () => number,
): number {
  return pickWeighted(remaining(counts, drawn, replace), rng);
}

/** n expériences complètes : combien réalisent l'événement ? */
export function simulate(
  setup: UrnSetup,
  id: EventId,
  c: number,
  n: number,
  rng: () => number,
): number {
  const ev = eventById(id);
  let hits = 0;
  for (let i = 0; i < n; i++) {
    const drawn: number[] = [];
    for (let j = 0; j < setup.k; j++) drawn.push(drawOne(setup.counts, drawn, setup.replace, rng));
    if (ev.test(drawn, c)) hits++;
  }
  return hits;
}
